/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.93, "tu3": 2, "notes": "Pure reducer for comment INSERT/UPDATE/DELETE realtime events plus the message-list helpers (active-stream test, TOAST-safe merge, optimistic replacement). No I/O, no store imports.", "tuVersion": "2.5"}
 * .help "Decides how a live message change (new, edited, deleted) updates the list of messages you are looking at — and ignores changes that belong to other rooms."
 *
 * Carved out of streamStore.initializeStreamMessages (Oct 2026). Side effects — touching the
 * chat session, waking agents, clearing a sender's typing indicator timer — stay in
 * stream/realtime.ts; this file only computes the next state.
 *
 * Tests: node --test frontend/src/store/stream/realtimeReducer.test.ts
 */
import type { Comment } from '../../types';
import type { ProposedAction } from '../../utils/agent';
import { buildStreamKey, upsertStreamCacheEntry, type StreamCacheEntry } from './streamCache.ts';

/* ----------------------------------------------------------------------------------------------
 * Message-list helpers (also used by streamStore for sends, refresh, and paging)
 * --------------------------------------------------------------------------------------------*/

export const sortAsc = (arr: Comment[]) =>
  [...arr].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

/**
 * Merge a realtime UPDATE over the message we already hold. Realtime omits unchanged TOASTed
 * columns (content > ~2 KB, large jsonb), so a reaction on a long message arrives without
 * `content`; blank/absent incoming values must never beat real existing ones.
 */
export function mergeSafe<
  T extends {
    id: string;
    content?: string;
    reactions?: Record<string, string[]>;
    sharedItemDetails?: unknown;
  },
>(existing: T | undefined, incoming: T): T {
  return {
    ...(existing ?? ({} as T)),
    ...incoming,
    content: incoming.content?.trim() ? incoming.content : existing?.content ?? incoming.content,
    reactions: incoming.reactions ?? existing?.reactions ?? {},
    sharedItemDetails: incoming.sharedItemDetails ?? existing?.sharedItemDetails,
  };
}

/** Does this row belong to the stream currently on screen? DMs match in either direction. */
export function belongsToActive(
  msg: Comment,
  activeType: Comment['streamType'] | undefined,
  activeRecipientId: string | undefined,
  me: string
) {
  if (!activeType || !activeRecipientId) return false;
  if (activeType === 'direct_message') {
    const them = activeRecipientId;
    return (
      msg.streamType === 'direct_message' &&
      (
        (msg.userId === me   && msg.recipientStreamId === them) ||
        (msg.userId === them && msg.recipientStreamId === me)
      )
    );
  }
  return msg.streamType === activeType && msg.recipientStreamId === activeRecipientId;
}

/** System payloads (help-gutter context updates) are for agents, never for people. */
export const isSystemPayload = (msg: Comment) =>
  !!msg.tags?.some((t) => t === 'system-notification' || t === 'context-update');

/**
 * Put a confirmed message into the list: replace the optimistic `temp-` copy of the same
 * text from the same sender if there is one, otherwise append (unless already present).
 */
export function replaceOptimisticOrAppend(list: Comment[], msg: Comment): Comment[] {
  const oi = list.findIndex(
    (m) => m.id.startsWith('temp-') && m.content === msg.content && m.userId === msg.userId
  );
  if (oi > -1) return list.map((m, i) => (i === oi ? msg : m));
  if (list.some((m) => m.id === msg.id)) return list;
  return [...list, msg];
}

/* ----------------------------------------------------------------------------------------------
 * Reducer
 * --------------------------------------------------------------------------------------------*/

export type CommentEvent =
  | { type: 'INSERT'; msg: Comment; actions: ProposedAction[] }
  | { type: 'UPDATE'; msg: Comment }
  | { type: 'DELETE'; msg: Comment };

/** The slice of stream state the reducer reads and writes. streamStore satisfies it structurally. */
export type ReducerState = {
  messages: Comment[];
  messageCacheByStream: Record<string, StreamCacheEntry>;
  hasMoreMessages: boolean;
  messageOffset: number;
  typingUsers: Record<string, number>;
  actionsByMessageId: Record<string, ProposedAction[]>;
  activeStreamType?: Comment['streamType'];
  activeRecipientStreamId?: string;
};

/**
 * Apply one realtime comment event. Returns the same object when the event is not for the
 * active stream (so zustand skips the update). `msg.content` for INSERT must already be
 * cleaned (agent metadata and fenced action JSON stripped) — see stream/realtime.ts.
 */
export function applyCommentEvent<S extends ReducerState>(s: S, ev: CommentEvent, me: string): S {
  const { activeStreamType, activeRecipientStreamId } = s;

  // DELETE payloads carry only the primary key (comments has REPLICA IDENTITY DEFAULT), so the
  // room test above would always fail. Being in the active list *is* the membership test.
  if (ev.type === 'DELETE') {
    if (!s.messages.some((m) => m.id === ev.msg.id)) return s;
  } else if (!belongsToActive(ev.msg, activeStreamType, activeRecipientStreamId, me)) {
    return s;
  }

  let list: Comment[];
  let typingUsers = s.typingUsers;
  let actionsByMessageId = s.actionsByMessageId;

  switch (ev.type) {
    case 'INSERT': {
      if (isSystemPayload(ev.msg)) return s;
      list = replaceOptimisticOrAppend(s.messages, ev.msg);
      // Whoever just posted is no longer typing.
      if (typingUsers[ev.msg.userId]) {
        typingUsers = { ...typingUsers };
        delete typingUsers[ev.msg.userId];
      }
      if (ev.actions.length) {
        actionsByMessageId = { ...actionsByMessageId, [ev.msg.id]: ev.actions };
      }
      break;
    }
    case 'UPDATE': {
      list = s.messages.map((m) => (m.id === ev.msg.id ? mergeSafe(m, ev.msg) : m));
      break;
    }
    case 'DELETE': {
      list = s.messages.filter((m) => m.id !== ev.msg.id);
      break;
    }
  }

  const messages = sortAsc(list);
  const cacheKey = buildStreamKey(activeStreamType, activeRecipientStreamId);
  return {
    ...s,
    messages,
    messageCacheByStream: cacheKey ? upsertStreamCacheEntry(s, cacheKey, messages) : s.messageCacheByStream,
    typingUsers,
    actionsByMessageId,
  };
}
